/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { styled, alpha } from "@mui/material/styles";
import { Box } from "@mui/material";
import { AwvInfo, AwvInfoConditionEnum } from "client";
import { Battery } from "components/TaskList/Battery";
import { ReactComponent as RunningIcon } from "assets/running_icon.svg";
import { ReactComponent as StopIcon } from "assets/stop_icon.svg";
import { ReactComponent as ErrorIcon } from "assets/error_icon.svg";
import { ReactComponent as OfflineIcon } from "assets/offline_icon.svg";
import { ReactComponent as OtaIcon } from "assets/OTA_icon.svg";

interface Props {
  awvInfo: AwvInfo;
}

const statusDict = {
  RUNNING: {
    iconSvg: <RunningIcon />,
    text: "AWVUI-MSG32",
  },
  STOP: {
    iconSvg: <StopIcon />,
    text: "AWVUI-MSG33",
  },
  OFFLINE: {
    iconSvg: <OfflineIcon />,
    text: "AWVUI-MSG82",
  },
  ERROR: {
    iconSvg: <ErrorIcon />,
    text: "AWVUI-MSG81",
  },
  CHARGING: {
    iconSvg: <StopIcon />,
    text: "AWVUI-MSG33",
  },
  OTA: {
    iconSvg: <StopIcon />,
    text: "AWVUI-MSG33",
  },
  DUMMY: {
    iconSvg: <OfflineIcon />,
    text: "AWVUI-MSG161",
  },
};

export const VehicleCard: FC<Props> = ({ awvInfo }) => {
  const { iconSvg, text } = statusDict[awvInfo.condition || "DUMMY"];
  const { t } = useTranslation();
  const [bgColor, setBgColor] = useState<string>("white");
  const [isOta, setIsOta] = useState(false);

  useEffect(() => {
    if (
      !awvInfo.condition ||
      awvInfo.condition === AwvInfoConditionEnum.Offline
    ) {
      setBgColor("#E6E6E6");
    } else if (awvInfo.condition === AwvInfoConditionEnum.Error) {
      setBgColor("#FFD9D9");
    } else if (awvInfo.condition === AwvInfoConditionEnum.Ota) {
      setBgColor("white");
      setIsOta(true);
    } else {
      setBgColor("white");
    }
  }, [awvInfo.condition]);

  return (
    <CustomContainer bg={bgColor}>
      <IdArea>
        <span>{t("AWVUI-MSG77")}</span>
        <label>{awvInfo.nameId ?? "--"}</label>
      </IdArea>
      <StatusArea>
        <StatusIcon>{iconSvg}</StatusIcon>
        <label>{t(text)}</label>
      </StatusArea>
      <Spacer />
      {isOta && <OtaIcon width={40} height={40} />}
      <Battery awvInfo={awvInfo} />
    </CustomContainer>
  );
};

interface CustomContainerProps {
  bg: string;
}

const CustomContainer = styled(Box)<CustomContainerProps>(({ theme, bg }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: 0,
  backgroundColor: bg,
  borderBottomWidth: 1,
  borderBottomStyle: "solid",
  borderBottomColor: alpha(theme.palette.common.black, 0.5),
}));

const IdArea = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  width: 60,
  height: 60,
  padding: theme.spacing(0.5),
  paddingBottom: theme.typography.caption.fontSize,
  background: alpha(theme.palette.common.black, 0.05),
  span: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1,
    textAlign: "left",
  },
  label: {
    flexGrow: 1,
    color: theme.palette.text.primary,
    fontSize: theme.typography.h4.fontSize,
    fontWeight: "normal",
    lineHeight: 1,
    textAlign: "center",
  },
}));

const StatusArea = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(0.5),
  img: {
    width: theme.typography.h6.fontSize,
  },
  label: {
    color: theme.palette.text.primary,
    fontSize: theme.typography.body1.fontSize,
    fontWeight: 500,
  },
}));

const StatusIcon = styled(Box)({
  width: 20,
  height: 20,
});

const Spacer = styled(Box)({
  flexGrow: 1,
});
